import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import customParseFormat from "dayjs/plugin/customParseFormat"

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(customParseFormat)

/**
 * @param dateStr         서버가 내려준 날짜 문자열.
 *    예) "2025-01-20T10:33:16.764972",
 *        "2025-01-20T10:33:16.764972Z",
 *        "2025-01-20T10:33:16.764972+09:00"
 * @param outputFormat    최종적으로 보여줄 포맷
 *    기본값: "YYYY년 MM월 DD일 HH:mm:ss"
 *
 * @description
 *   - 입력에 이미 'Z' 또는 ±hh:mm 오프셋이 있으면 그대로 사용
 *   - 오프셋이 없다면 (e.g. "2025-01-20T10:33:16.764972") → 'Z'(UTC) 붙여서 해석
 *   - dayjs 로 파싱한 뒤, KST(Asia/Seoul)로 변환
 *   - outputFormat 으로 최종 문자열 반환
 */
const utcToKoreanDateTime = (
  dateStr: string,
  outputFormat = "YYYY년 MM월 DD일 HH:mm:ss",
): string => {
  // 0) Java ZonedDateTime 형식의 [UTC], [Asia/Seoul] 등 제거
  //    예: "2025-10-02T01:58:40.119531207Z[UTC]" -> "2025-10-02T01:58:40.119531207Z"
  const cleanedDateStr = dateStr.replace(/\[.*?\]$/, "")

  // 1) 문자열 끝에 'Z' 또는 ±hh:mm 이 있는지 확인
  const hasTimeZoneInfo = /(Z|[+\-]\d{2}:\d{2})$/.test(cleanedDateStr)

  // 2) 오프셋이 없으면 => 'Z' 붙여서 "UTC 시각"으로 간주
  const dateString = hasTimeZoneInfo ? cleanedDateStr : `${cleanedDateStr}Z`

  // 3) dayjs 로 파싱 → 'tz("Asia/Seoul")'로 한국 시간 변환
  //    (오프셋이 있으면 그에 맞춰 파싱, 없으면 'Z'를 추가해 UTC로 파싱)
  const parsed = dayjs(dateString)

  // 4) 파싱이 실패했거나 유효하지 않은 날짜면 원본 반환
  if (!parsed.isValid()) {
    return dateStr
  }

  const kst = parsed.tz("Asia/Seoul")

  // 5) 타임존 변환 후에도 유효성 재확인
  if (!kst.isValid()) {
    return dateStr
  }

  // 6) 원하는 포맷으로 반환
  const formatted = kst.format(outputFormat)

  // 7) 포맷팅 결과가 비어있거나 유효하지 않으면 원본 반환
  if (!formatted || formatted.includes("Invalid")) {
    return dateStr
  }

  return formatted
}

export default utcToKoreanDateTime
